<template>
  <div class="wds3-editor">
    <div class="picker-container">

      <!-- Header Section -->
      <div class="header-section">
        <div class="logo-container">
          <div class="logo-icon">⚙️</div>
          <div>
            <h1 class="main-title">WDS Data Editor</h1>
            <span class="version-badge">V3</span>
          </div>
        </div>
        <p class="subtitle">Select a game you administrate to edit its data files</p>
      </div>

      <!-- Loading Games -->
      <div v-if="loadingGames" class="status-card">
        <div class="spinner"></div>
        <p class="status-text">Loading your games...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="loadError" class="error-card">
        <div class="error-icon">⚠️</div>
        <p class="error-text">{{ loadError }}</p>
        <button @click="fetchAdminGames" class="primary-button">
          <span>Retry</span>
        </button>
      </div>

      <!-- Game Selector -->
      <div v-else class="selection-card">
        <div class="card-icon">🎮</div>
        <h2 class="card-title">Select a Game</h2>
        <p class="card-description">
          Choose from your admin games below. All data files will be downloaded into memory for editing.
        </p>

        <div v-if="games.length === 0" class="no-games">
          <p>You are not a game admin for any games yet.</p>
        </div>

        <div v-else class="selector-row">
          <select v-model="selectedGame" class="game-select">
            <option :value="null" disabled>Choose a game...</option>
            <option v-for="g in games" :key="g.IdGame" :value="g">
              {{ g.GameName }}
            </option>
          </select>

          <button
            @click="loadGame"
            :disabled="!selectedGame || downloading"
            class="primary-button load-btn"
          >
            <span v-if="downloading" class="spinner-inline"></span>
            <span>{{ downloading ? 'Loading...' : 'Load Game' }}</span>
          </button>
        </div>

        <!-- Download Progress -->
        <div v-if="downloading" class="progress-section">
          <div class="progress-bar-track">
            <div class="progress-bar-fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
          <p class="progress-label">{{ progressLabel }}</p>
        </div>

        <!-- Download Error -->
        <div v-if="downloadError" class="download-error">
          <span class="error-icon-inline">⚠️</span>
          {{ downloadError }}
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { wds3Cache } from '@/utils/wds3Cache';

export default {
  name: 'WDS3DataEditor',
  data() {
    return {
      games: [],
      selectedGame: null,
      loadingGames: false,
      loadError: null,
      downloading: false,
      downloadError: null,
      progressLabel: '',
      progressPercent: 0,
    };
  },
  async mounted() {
    // If the user refreshed while in the editor, restore the previous session.
    if (wds3Cache.restoreFromSession()) {
      await this.$router.push({
        name: 'WDS2UnitEditor',
        params: { gameName: wds3Cache.gameName, factionName: null },
      });
      history.replaceState({ ...(history.state || {}), wds3Mode: true }, '', '/wds3-editor');
      return;
    }
    await this.fetchAdminGames();
  },
  methods: {
    async fetchAdminGames() {
      this.loadingGames = true;
      this.loadError = null;
      try {
        const res = await fetch('/GetMyAdminGames/', {
          method: 'GET',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`Server error ${res.status}`);
        const data = await res.json();
        if (data.success) {
          this.games = data.games || [];
        } else {
          this.loadError = data.error || 'Failed to load your games.';
        }
      } catch (err) {
        console.error('Failed to fetch admin games:', err);
        this.loadError = 'Could not load your games. Please check your connection and try again.';
      } finally {
        this.loadingGames = false;
      }
    },

    async loadGame() {
      if (!this.selectedGame || this.downloading) return;
      this.downloading = true;
      this.downloadError = null;
      this.progressPercent = 0;
      this.progressLabel = 'Fetching manifest...';

      try {
        const manifestUrl = this.selectedGame.GameVersionDataPath;
        if (!manifestUrl) throw new Error('This game has no data path configured.');

        // Derive the base URL for all file paths
        const dataBasePath = manifestUrl.replace(/manifest\.json$/i, '');

        // Add cache-busting parameter to force fresh download from GitHub CDN + browser cache bypass
        const cacheBust = `?t=${Date.now()}`;
        const fetchOptions = { cache: 'no-store' }; // Bypass browser cache
        
        console.log('[WDS3DataEditor] Cache-busting parameters:', {
          cacheBust,
          fetchOptions,
          manifestUrl: manifestUrl + cacheBust
        });

        // Fetch and parse manifest
        const manifestRes = await fetch(manifestUrl + cacheBust, fetchOptions);
        if (!manifestRes.ok) throw new Error(`Failed to fetch manifest (${manifestRes.status})`);
        const manifest = await manifestRes.json();

        const files = manifest.files || [];
        if (files.length === 0) throw new Error('Manifest contains no files.');

        // Reset cache
        wds3Cache.gameId = this.selectedGame.IdGame;
        wds3Cache.gameName = this.selectedGame.GameName;
        wds3Cache.dataBasePath = dataBasePath;
        wds3Cache.manifest = manifest;
        wds3Cache.gameFiles = {};
        wds3Cache.factions = [];
        
        // Set base version from the downloaded manifest
        console.log('[WDS3DataEditor] Setting baseVersion from manifest:', {
          manifestVersion: manifest.version,
          manifestType: typeof manifest.version,
          manifest: manifest
        });
        wds3Cache.baseVersion = manifest.version;
        console.log('[WDS3DataEditor] baseVersion set to:', wds3Cache.baseVersion);

        // Build a map of faction names → their file map
        const factionMap = {};

        // Download all files concurrently
        this.progressLabel = `Downloading 0 / ${files.length} files...`;
        let done = 0;

        await Promise.all(
          files.map(async (relPath) => {
            const url = dataBasePath + relPath + cacheBust;
            try {
              const fileRes = await fetch(url, fetchOptions);
              const parsed = fileRes.ok ? await fileRes.json() : [];

              const slashIdx = relPath.indexOf('/');
              if (slashIdx !== -1) {
                // Faction file: "Sample Army/units.json"
                const factionName = relPath.substring(0, slashIdx);
                const fileName = relPath.substring(slashIdx + 1);
                if (!factionMap[factionName]) factionMap[factionName] = {};
                factionMap[factionName][fileName] = parsed;
              } else {
                // Game-level file
                wds3Cache.gameFiles[relPath] = parsed;
              }
            } catch {
              // Store empty array for missing/corrupt files — editor handles gracefully
              const slashIdx = relPath.indexOf('/');
              if (slashIdx !== -1) {
                const factionName = relPath.substring(0, slashIdx);
                const fileName = relPath.substring(slashIdx + 1);
                if (!factionMap[factionName]) factionMap[factionName] = {};
                factionMap[factionName][fileName] = [];
              } else {
                wds3Cache.gameFiles[relPath] = [];
              }
            } finally {
              done++;
              this.progressPercent = Math.round((done / files.length) * 100);
              this.progressLabel = `Downloading ${done} / ${files.length} files...`;
            }
          })
        );

        // Flatten faction map into the cache factions array
        wds3Cache.factions = Object.entries(factionMap).map(([name, filesObj]) => ({
          name,
          files: filesObj,
        }));

        // Persist to sessionStorage so a page refresh restores the session.
        wds3Cache.saveToSession();

        this.progressLabel = 'Done! Launching editor...';
        this.progressPercent = 100;

        // Navigate to WDS2UnitEditor with V3 mode flag
        await this.$router.push({
          name: 'WDS2UnitEditor',
          params: {
            gameName: this.selectedGame.GameName,
            factionName: null,
          },
        });

        // Keep the browser URL at /wds3-editor instead of the internal /editor2/… route.
        history.replaceState(
          { ...(history.state || {}), wds3Mode: true },
          '',
          '/wds3-editor'
        );
      } catch (err) {
        console.error('V3 game load failed:', err);
        this.downloadError = err.message || 'Failed to load game data. Please try again.';
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>

<style scoped>
.wds3-editor {
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  padding: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.picker-container {
  max-width: 680px;
  width: 100%;
}

/* Header */
.header-section {
  text-align: center;
  margin-bottom: 3rem;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.logo-icon {
  font-size: 3rem;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.main-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: #f1f5f9;
  margin: 0;
  text-shadow: 0 2px 10px rgba(59, 130, 246, 0.5);
  line-height: 1.1;
}

.version-badge {
  display: inline-block;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  letter-spacing: 0.05em;
  vertical-align: middle;
  margin-top: 0.25rem;
}

.subtitle {
  font-size: 1.125rem;
  color: #94a3b8;
  margin: 0;
}

/* Cards */
.selection-card,
.status-card,
.error-card {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-radius: 1rem;
  padding: 2.5rem;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid #475569;
  transition: all 0.3s ease;
}

.selection-card:hover {
  box-shadow: 0 15px 50px rgba(59, 130, 246, 0.2);
  border-color: #3b82f6;
}

.card-icon {
  font-size: 3.5rem;
  margin-bottom: 1rem;
}

.card-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 0.75rem;
}

.card-description {
  color: #94a3b8;
  margin-bottom: 2rem;
  line-height: 1.6;
}

/* Status / Spinner */
.status-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem;
}

.spinner {
  width: 2.5rem;
  height: 2.5rem;
  border: 3px solid #334155;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.spinner-inline {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 0.4rem;
  vertical-align: middle;
}

.status-text {
  color: #94a3b8;
  font-size: 1rem;
}

/* Error */
.error-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2.5rem;
}

.error-icon {
  font-size: 3rem;
}

.error-text {
  color: #f87171;
  font-size: 1rem;
  margin: 0;
}

/* Selector Row */
.selector-row {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}

.game-select {
  flex: 1;
  min-width: 220px;
  max-width: 340px;
  background: #0f172a;
  color: #f1f5f9;
  border: 1px solid #475569;
  border-radius: 0.5rem;
  padding: 0.65rem 1rem;
  font-size: 0.95rem;
  outline: none;
  transition: border-color 0.2s;
  cursor: pointer;
}

.game-select:focus {
  border-color: #3b82f6;
}

.no-games {
  color: #94a3b8;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
}

/* Primary Button */
.primary-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff;
  border: none;
  border-radius: 0.6rem;
  padding: 0.65rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.primary-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #60a5fa, #3b82f6);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
}

.primary-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.load-btn {
  flex-shrink: 0;
}

/* Progress */
.progress-section {
  margin-top: 1.25rem;
}

.progress-bar-track {
  background: #0f172a;
  border-radius: 999px;
  height: 0.5rem;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #6366f1);
  border-radius: 999px;
  transition: width 0.2s ease;
}

.progress-label {
  color: #94a3b8;
  font-size: 0.85rem;
  margin: 0;
}

/* Download error */
.download-error {
  margin-top: 1rem;
  color: #f87171;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

.error-icon-inline {
  font-size: 1rem;
}
</style>
